import {
  CmpManageNewsletterRowReturn,
  CmpOutboundEmailFieldsFragment,
  CmpOutboundEmailStatusEnum,
  CmpNewsletterDataInput,
  CmpNewsletterFieldsFragment,
  CmpNewsletterNewsroomStatusEnum,
  CmpNewsletterRecipientGroupFieldsFragment,
  CmpNewsletterRecipientSelectionInput,
  CmpNewsletterRecipientSourceKindEnum,
  CmpNewsletterSenderReadinessStatusEnum,
  CmpNewsletterStatusEnum,
  CmpNewsletterTypeEnum,
  PreviewCmpNewsletterRecipientsQuery,
} from "../../../graphql/types";
import {
  EmailFooterProfile,
  EmailTemplateKey,
  getEmailTemplatePreset,
  parseEmailBuilderStateJson,
  renderEmailBuilderState,
} from "./emailBuilderData";
import {
  EmailCampaign,
  EmailDeliverySummary,
  EmailFormState,
  EmailOutboundRecord,
  EmailOutboundStatus,
  EmailStatus,
  EmailTemplateSelection,
  EmailType,
  NewsroomStatus,
  RecipientGroup,
  RecipientPreviewSummary,
  RecipientSourceId,
  buildDefaultCreateEmailForm,
} from "./emailMockData";
import { cmpLongDateFormatter, cmpLongDateTimeFormatter } from "../../common/dashboard/formatters";

type ManageNewsletterRow = Pick<
  CmpManageNewsletterRowReturn,
  | "id"
  | "slug"
  | "subject"
  | "emailType"
  | "status"
  | "newsroomStatus"
  | "scheduledAtUtcMilli"
  | "sentAtUtcMilli"
  | "publishedAtUtcMilli"
  | "updatedAtUtcMilli"
  | "estimatedRecipientCount"
  | "invalidRecipientCount"
  | "skippedRecipientCount"
  | "sentRecipientCount"
  | "failedRecipientCount"
  | "skippedDeliveryCount"
  | "attachmentCount"
  | "openCount"
  | "clickCount"
>;

const dateFormatter = cmpLongDateFormatter;

const dateTimeFormatter = cmpLongDateTimeFormatter;

const formatDate = (utcMilli?: number | null) =>
  utcMilli ? dateFormatter.format(new Date(utcMilli)) : undefined;

const formatDateTime = (utcMilli?: number | null) =>
  utcMilli ? dateTimeFormatter.format(new Date(utcMilli)) : undefined;

const formatDateTimeLabel = (utcMilli?: number | null) =>
  formatDateTime(utcMilli) || "";

const toLocalDateTimeInputParts = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return { date: "", time: "" };
  }

  const date = new Date(utcMilli);
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  const value = localDate.toISOString();

  return {
    date: value.slice(0, 10),
    time: value.slice(11, 16),
  };
};

const toUtcMilli = (date: string, time: string): number | null => {
  if (!date || !time) {
    return null;
  }

  const parsed = new Date(`${date}T${time}`).getTime();

  return Number.isFinite(parsed) ? parsed : null;
};

export const emailSchedulePartsToUtcMilli = toUtcMilli;

const splitCommaList = (value: string): string[] =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

export const splitDirectRecipients = (value: string): string[] =>
  value
    .split(/[\s,;]+/)
    .map((recipient) => recipient.trim())
    .filter(Boolean);

const htmlEscape = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const textToHtml = (value: string): string =>
  value
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${htmlEscape(paragraph).replace(/\n/g, "<br />")}</p>`)
    .join("");

const htmlToText = (value?: string | null): string =>
  (value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();

const formatFileSize = (sizeBytes: number): string => {
  if (sizeBytes < 1024 * 1024) {
    return `${Math.max(sizeBytes / 1024, 1).toFixed(0)} KB`;
  }

  return `${(sizeBytes / 1024 / 1024).toFixed(2)} MB`;
};

export const formatAttachmentFileSize = formatFileSize;

export const buildEmailDuplicateDraft = (
  email: EmailFormState
): EmailFormState => ({
  ...email,
  id: undefined,
  status: "draft",
  subject: `${email.subject || "Untitled email"} copy`,
  attachments: [],
  publishToNewsroom: false,
  newsroomStatus: "notPublished",
  newsroomSlug: "",
});

const outboundStatusFromApi = (
  status: CmpOutboundEmailStatusEnum
): EmailOutboundStatus => {
  switch (status) {
    case CmpOutboundEmailStatusEnum.Failed:
      return "failed";
    case CmpOutboundEmailStatusEnum.Skipped:
      return "skipped";
    case CmpOutboundEmailStatusEnum.Sent:
    default:
      return "sent";
  }
};

const outboundKindLabel = (kind: string): string =>
  kind
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const outboundEmailReturnToUi = (
  outboundEmail: CmpOutboundEmailFieldsFragment
): EmailOutboundRecord => ({
  id: outboundEmail.id,
  kind: outboundKindLabel(outboundEmail.kind),
  status: outboundStatusFromApi(outboundEmail.status),
  toEmail: outboundEmail.toEmail,
  intendedToEmail: outboundEmail.intendedToEmail || "",
  recipientOverrideUsed: outboundEmail.recipientOverrideUsed,
  subject: outboundEmail.subject,
  errorMessage: outboundEmail.errorMessage || "",
  attachmentCount: outboundEmail.attachmentCount,
  createdAt: formatDateTimeLabel(outboundEmail.createdAtUtcMilli),
  sentAt: formatDateTimeLabel(outboundEmail.sentAtUtcMilli),
  failedAt: formatDateTimeLabel(outboundEmail.failedAtUtcMilli),
  openedAt: formatDateTimeLabel(outboundEmail.openedAtUtcMilli),
  clickedAt: formatDateTimeLabel(outboundEmail.clickedAtUtcMilli),
});

const deliverySummaryReturnToUi = (
  newsletter: CmpNewsletterFieldsFragment
): EmailDeliverySummary | null =>
  newsletter.deliverySummary
    ? {
        totalOutboundEmailCount:
          newsletter.deliverySummary.totalOutboundEmailCount,
        sentCount: newsletter.deliverySummary.sentCount,
        failedCount: newsletter.deliverySummary.failedCount,
        skippedCount: newsletter.deliverySummary.skippedCount,
        intendedRecipientCount:
          newsletter.deliverySummary.intendedRecipientCount,
        actualRecipientCount: newsletter.deliverySummary.actualRecipientCount,
        recipientOverrideUsed: newsletter.deliverySummary.recipientOverrideUsed,
        latestOutboundEmailAt: formatDateTimeLabel(
          newsletter.deliverySummary.latestOutboundEmailAtUtcMilli
        ),
      }
    : null;

export const buildSlugFromSubject = (subject: string) =>
  subject
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72);

export const emailTypeToApi = (emailType: EmailType): CmpNewsletterTypeEnum => {
  switch (emailType) {
    case "pressRelease":
      return CmpNewsletterTypeEnum.PressRelease;
    case "bulletin":
      return CmpNewsletterTypeEnum.Bulletin;
    case "eBlast":
      return CmpNewsletterTypeEnum.EBlast;
    case "announcement":
      return CmpNewsletterTypeEnum.Announcement;
    // advocacyUpdate / memberWelcome / fromTheDesk / customEmail persist as
    // NEWSLETTER until CmpNewsletterTypeEnum grows matching members (backend
    // contract gap — the selected template still round-trips via templateKey).
    case "newsletter":
    default:
      return CmpNewsletterTypeEnum.Newsletter;
  }
};

export const emailTypeFromApi = (emailType: CmpNewsletterTypeEnum): EmailType => {
  switch (emailType) {
    case CmpNewsletterTypeEnum.PressRelease:
      return "pressRelease";
    case CmpNewsletterTypeEnum.Bulletin:
      return "bulletin";
    case CmpNewsletterTypeEnum.EBlast:
      return "eBlast";
    case CmpNewsletterTypeEnum.Announcement:
      return "announcement";
    case CmpNewsletterTypeEnum.Newsletter:
    default:
      return "newsletter";
  }
};

export const statusToApi = (status: EmailStatus): CmpNewsletterStatusEnum => {
  switch (status) {
    case "review":
      return CmpNewsletterStatusEnum.ReadyForReview;
    case "scheduled":
      return CmpNewsletterStatusEnum.Scheduled;
    case "sending":
      return CmpNewsletterStatusEnum.Sending;
    case "sent":
      return CmpNewsletterStatusEnum.Sent;
    case "canceled":
      return CmpNewsletterStatusEnum.Canceled;
    case "failed":
      return CmpNewsletterStatusEnum.Failed;
    case "draft":
    default:
      return CmpNewsletterStatusEnum.Draft;
  }
};

export const statusFromApi = (status: CmpNewsletterStatusEnum): EmailStatus => {
  switch (status) {
    case CmpNewsletterStatusEnum.ReadyForReview:
      return "review";
    case CmpNewsletterStatusEnum.Scheduled:
      return "scheduled";
    case CmpNewsletterStatusEnum.Sending:
      return "sending";
    case CmpNewsletterStatusEnum.Sent:
      return "sent";
    case CmpNewsletterStatusEnum.Canceled:
      return "canceled";
    case CmpNewsletterStatusEnum.Failed:
      return "failed";
    case CmpNewsletterStatusEnum.Archived:
    case CmpNewsletterStatusEnum.Draft:
    default:
      return "draft";
  }
};

export const newsroomStatusFromApi = (
  status: CmpNewsletterNewsroomStatusEnum
): NewsroomStatus => {
  switch (status) {
    case CmpNewsletterNewsroomStatusEnum.Published:
      return "published";
    case CmpNewsletterNewsroomStatusEnum.Unpublished:
      return "unpublished";
    case CmpNewsletterNewsroomStatusEnum.NotPublished:
    default:
      return "notPublished";
  }
};

export const newsroomStatusToApi = (
  status: NewsroomStatus
): CmpNewsletterNewsroomStatusEnum => {
  switch (status) {
    case "published":
      return CmpNewsletterNewsroomStatusEnum.Published;
    case "unpublished":
      return CmpNewsletterNewsroomStatusEnum.Unpublished;
    case "notPublished":
    default:
      return CmpNewsletterNewsroomStatusEnum.NotPublished;
  }
};

const sourceKindByRecipientSourceId: Record<
  RecipientSourceId,
  CmpNewsletterRecipientSourceKindEnum
> = {
  activeMembers: CmpNewsletterRecipientSourceKindEnum.ActiveMembers,
  allMembers: CmpNewsletterRecipientSourceKindEnum.AllMembers,
  nonMemberSubscribers:
    CmpNewsletterRecipientSourceKindEnum.NonMemberSubscribers,
  newMembers: CmpNewsletterRecipientSourceKindEnum.NewMembers,
  renewingMembers: CmpNewsletterRecipientSourceKindEnum.RenewingMembers,
  prospectMembers: CmpNewsletterRecipientSourceKindEnum.ProspectMembers,
  eventAttendees: CmpNewsletterRecipientSourceKindEnum.EventAttendees,
  pastEventAttendees: CmpNewsletterRecipientSourceKindEnum.PastEventAttendees,
  eblastSubscribers: CmpNewsletterRecipientSourceKindEnum.WebsiteSubscribers,
  directRecipients: CmpNewsletterRecipientSourceKindEnum.DirectEmails,
  selectedMembers: CmpNewsletterRecipientSourceKindEnum.SelectedMembers,
};

const recipientSourceIdBySourceKind: Partial<
  Record<CmpNewsletterRecipientSourceKindEnum, RecipientSourceId>
> = {
  [CmpNewsletterRecipientSourceKindEnum.ActiveMembers]: "activeMembers",
  [CmpNewsletterRecipientSourceKindEnum.AllMembers]: "allMembers",
  [CmpNewsletterRecipientSourceKindEnum.NonMemberSubscribers]:
    "nonMemberSubscribers",
  [CmpNewsletterRecipientSourceKindEnum.NewMembers]: "newMembers",
  [CmpNewsletterRecipientSourceKindEnum.RenewingMembers]: "renewingMembers",
  [CmpNewsletterRecipientSourceKindEnum.ProspectMembers]: "prospectMembers",
  [CmpNewsletterRecipientSourceKindEnum.EventAttendees]: "eventAttendees",
  [CmpNewsletterRecipientSourceKindEnum.PastEventAttendees]: "pastEventAttendees",
  [CmpNewsletterRecipientSourceKindEnum.WebsiteSubscribers]: "eblastSubscribers",
  [CmpNewsletterRecipientSourceKindEnum.DirectEmails]: "directRecipients",
  [CmpNewsletterRecipientSourceKindEnum.SelectedMembers]: "selectedMembers",
};

export const formStateToRecipientSelectionInput = (
  email: EmailFormState
): CmpNewsletterRecipientSelectionInput => {
  const directEmails = splitDirectRecipients(email.directRecipients);
  const recipientSourceIds = [...email.recipientSourceIds];

  if (
    email.selectedMemberIds.length > 0 &&
    !recipientSourceIds.includes("selectedMembers")
  ) {
    recipientSourceIds.push("selectedMembers");
  }

  if (
    directEmails.length > 0 &&
    !recipientSourceIds.includes("directRecipients")
  ) {
    recipientSourceIds.push("directRecipients");
  }

  return {
    sourceKinds: Array.from(
      new Set(
        recipientSourceIds.map(
          (sourceId) => sourceKindByRecipientSourceId[sourceId]
        )
      )
    ),
    groupIds: email.recipientGroupIds,
    memberIds: email.selectedMemberIds,
    eventIds: [],
    directEmails,
    memberFilters: {
      membershipTypeIds: email.memberFilters.membershipTypeIds,
      employeeRangeIds: email.memberFilters.employeeRangeIds,
      categoryIds: email.memberFilters.categoryIds,
      memberSinceRangeIds: email.memberFilters.memberSinceRangeIds,
      renewalRangeIds: email.memberFilters.renewalRangeIds,
    },
  };
};

const sortRecipientSelectionValues = <Value extends string>(
  values: Value[] | null | undefined
): Value[] => [...(values || [])].sort();

// Audience preview requests depend only on recipient selection. Canonicalizing
// every array prevents unrelated draft edits or selection ordering from
// restarting the debounced backend preview.
export const getEmailAudienceSelectionKey = (email: EmailFormState): string => {
  const selection = formStateToRecipientSelectionInput(email);
  const memberFilters = selection.memberFilters;

  return JSON.stringify({
    sourceKinds: sortRecipientSelectionValues(selection.sourceKinds),
    groupIds: sortRecipientSelectionValues(selection.groupIds),
    memberIds: sortRecipientSelectionValues(selection.memberIds),
    eventIds: sortRecipientSelectionValues(selection.eventIds),
    directEmails: sortRecipientSelectionValues(
      selection.directEmails?.map((emailAddress) => emailAddress.toLowerCase())
    ),
    memberFilters: {
      membershipTypeIds: sortRecipientSelectionValues(
        memberFilters?.membershipTypeIds
      ),
      employeeRangeIds: sortRecipientSelectionValues(
        memberFilters?.employeeRangeIds
      ),
      categoryIds: sortRecipientSelectionValues(memberFilters?.categoryIds),
      memberSinceRangeIds: sortRecipientSelectionValues(
        memberFilters?.memberSinceRangeIds
      ),
      renewalRangeIds: sortRecipientSelectionValues(
        memberFilters?.renewalRangeIds
      ),
    },
  });
};

export const emailFormStateToInput = (
  email: EmailFormState,
  footerProfile?: EmailFooterProfile | null
): CmpNewsletterDataInput => {
  const scheduledAtUtcMilli = toUtcMilli(email.scheduledDate, email.scheduledTime);
  const builderContent = renderEmailBuilderState(
    email.builderState,
    footerProfile || undefined
  );
  // Body and footer are stored separately so the backend send wrapper
  // (hidden preheader + footer append) does not double the footer block.
  const contentHtml = builderContent.bodyHtml || textToHtml(email.content);
  const contentText = builderContent.text || email.content;

  return {
    id: email.id,
    subject: email.subject.trim() || "Untitled email",
    preheader: email.preheader.trim() || undefined,
    fromName: email.fromName.trim() || undefined,
    fromEmail: email.fromEmail.trim() || undefined,
    replyToEmail: email.replyToEmail.trim() || undefined,
    emailType: emailTypeToApi(email.emailType),
    status: statusToApi(email.status === "sent" ? "draft" : email.status),
    scheduledAtUtcMilli,
    templateKey: email.templateId || undefined,
    builderStateJson: JSON.stringify(email.builderState),
    contentHtml,
    contentText,
    footerHtml: builderContent.footerHtml || undefined,
    recipientSelection: formStateToRecipientSelectionInput(email),
    newsroom: {
      publishToNewsroom: email.publishToNewsroom,
      slug: email.newsroomSlug.trim() || undefined,
      title: email.seoTitle.trim() || email.subject.trim() || undefined,
      summary: email.seoDescription.trim() || email.preheader.trim() || undefined,
      body: contentHtml || undefined,
      category: email.category.trim() || undefined,
      tags: splitCommaList(email.tags),
      authorName: email.fromName.trim() || undefined,
    },
    attachments: email.attachments.map((attachment) => ({
      id: attachment.id,
      fileName: attachment.name,
      mimeType: attachment.fileType || "application/octet-stream",
      sizeBytes: attachment.sizeBytes,
      storageKey: attachment.storageKey,
    })),
  };
};

// Template keys stored before the 8-template gallery (version-1 wizard) map
// to their nearest current template; anything unknown falls back to Custom
// Email so the saved canvas stays editable.
const legacyTemplateKeyMap: Record<string, EmailTemplateKey> = {
  chamberNewsletter: "newsletter",
  mediaRelease: "mediaRelease",
  advocacyUpdate: "advocacyUpdate",
  fromTheDesk: "fromTheDesk",
};

const templateIdFromStoredKey = (
  templateKey?: string | null
): EmailTemplateSelection => {
  // No stored key = the campaign never picked a template, so Step 3 must
  // land on the template GALLERY, not an empty builder canvas.
  if (!templateKey) {
    return "";
  }
  if (getEmailTemplatePreset(templateKey)) {
    return templateKey as EmailTemplateKey;
  }

  return legacyTemplateKeyMap[templateKey] || "customEmail";
};

export const newsletterReturnToFormState = (
  newsletter: CmpNewsletterFieldsFragment
): EmailFormState => {
  const scheduled = toLocalDateTimeInputParts(newsletter.scheduledAtUtcMilli);
  const recipientSourceIds = newsletter.recipientSelection.sourceKinds
    .map((sourceKind) => recipientSourceIdBySourceKind[sourceKind])
    .filter(Boolean) as RecipientSourceId[];
  const content =
    newsletter.contentText?.trim() ||
    htmlToText(newsletter.contentHtml) ||
    htmlToText(newsletter.newsroom.body);
  const builderState = parseEmailBuilderStateJson({
    builderStateJson: newsletter.builderStateJson,
    chamberName: newsletter.fromName || undefined,
    fallbackContent: content,
  });

  return {
    ...buildDefaultCreateEmailForm({
      chamberName: newsletter.fromName || undefined,
      senderEmail: newsletter.fromEmail,
      replyToEmail: newsletter.replyToEmail || undefined,
    }),
    id: newsletter.id,
    status: statusFromApi(newsletter.status),
    subject: newsletter.subject,
    preheader: newsletter.preheader || "",
    fromName: newsletter.fromName || "",
    fromEmail: newsletter.fromEmail,
    replyToEmail: newsletter.replyToEmail || "",
    emailType: emailTypeFromApi(newsletter.emailType),
    category: newsletter.newsroom.category || "",
    tags: newsletter.newsroom.tags.join(", "),
    recipientSourceIds,
    memberFilters: {
      membershipTypeIds:
        newsletter.recipientSelection.memberFilters.membershipTypeIds,
      employeeRangeIds:
        newsletter.recipientSelection.memberFilters.employeeRangeIds,
      categoryIds: newsletter.recipientSelection.memberFilters.categoryIds,
      memberSinceRangeIds:
        newsletter.recipientSelection.memberFilters.memberSinceRangeIds,
      renewalRangeIds:
        newsletter.recipientSelection.memberFilters.renewalRangeIds,
    },
    recipientGroupIds: newsletter.recipientSelection.groupIds,
    selectedMemberIds: newsletter.recipientSelection.memberIds,
    directRecipients: newsletter.recipientSelection.directEmails.join(", "),
    templateId: templateIdFromStoredKey(newsletter.templateKey),
    content,
    builderState,
    attachments: newsletter.attachments.map((attachment) => ({
      id: attachment.id,
      name: attachment.fileName,
      fileType: attachment.mimeType,
      sizeBytes: attachment.sizeBytes,
      sizeLabel: formatFileSize(attachment.sizeBytes),
      storageKey: attachment.storageKey || undefined,
      eTag: attachment.eTag,
    })),
    publishToNewsroom: newsletter.newsroom.publishToNewsroom,
    newsroomStatus: newsroomStatusFromApi(newsletter.newsroom.status),
    newsroomSlug: newsletter.newsroom.slug || "",
    seoTitle: newsletter.newsroom.title || newsletter.subject,
    seoDescription: newsletter.newsroom.summary || newsletter.preheader || "",
    scheduledDate: scheduled.date,
    scheduledTime: scheduled.time,
    estimatedRecipientCount: newsletter.estimatedRecipientCount,
    invalidRecipientCount: newsletter.invalidRecipientCount,
    skippedRecipientCount: newsletter.skippedRecipientCount,
    sentRecipientCount: newsletter.sentRecipientCount,
    failedRecipientCount: newsletter.failedRecipientCount,
    skippedDeliveryCount: newsletter.skippedDeliveryCount,
    openCount: newsletter.openCount,
    clickCount: newsletter.clickCount,
    outboundEmails: (newsletter.outboundEmails || []).map(outboundEmailReturnToUi),
    deliverySummary: deliverySummaryReturnToUi(newsletter),
    senderReadinessLabel:
      newsletter.senderReadiness.status ===
      CmpNewsletterSenderReadinessStatusEnum.Verified
        ? "Verified"
        : newsletter.senderReadiness.status ===
            CmpNewsletterSenderReadinessStatusEnum.FallbackAvailable
          ? "Fallback available"
          : "Blocked",
    senderReadinessDetail:
      newsletter.senderReadiness.blockingReason ||
      newsletter.senderReadiness.warning ||
      "Sender-domain readiness checked.",
  };
};

export const recipientGroupReturnToUiGroup = (
  group: CmpNewsletterRecipientGroupFieldsFragment
): RecipientGroup => ({
  id: group.id,
  name: group.name,
  description: group.description || "Staff-created recipient group.",
  count: group.estimatedRecipientCount,
  source:
    group.sourceKinds.length > 0
      ? group.sourceKinds
          .map((sourceKind) => sourceKind.replace(/_/g, " ").toLowerCase())
          .join(", ")
      : "Manual group",
  isArchived: group.isArchived,
});

export const manageNewsletterRowToEmailCampaign = (
  row: ManageNewsletterRow
): EmailCampaign => ({
  id: row.id,
  slug: row.slug,
  subject: row.subject,
  emailType: emailTypeFromApi(row.emailType),
  // The manage-row return does not carry the campaign name yet (backend gap:
  // CmpManageNewsletterRowReturn has no category/campaignName field), so the
  // table's Campaign Name column falls back to the subject instead of
  // rendering a raw email-type token.
  category: "",
  status: statusFromApi(row.status),
  newsroomStatus: newsroomStatusFromApi(row.newsroomStatus),
  scheduledAt: formatDateTime(row.scheduledAtUtcMilli),
  sentAt: formatDateTime(row.sentAtUtcMilli),
  publishedAt: formatDate(row.publishedAtUtcMilli),
  intendedRecipients: row.estimatedRecipientCount,
  invalidRecipients: row.invalidRecipientCount,
  skippedCandidateRecipients: row.skippedRecipientCount,
  sentRecipients: row.sentRecipientCount,
  skippedRecipients: row.skippedRecipientCount,
  failedRecipients: row.failedRecipientCount,
  skippedDeliveries: row.skippedDeliveryCount,
  openCount: row.openCount,
  clickCount: row.clickCount,
  attachmentCount: row.attachmentCount,
  updatedAt: formatDate(row.updatedAtUtcMilli) || "Not saved yet",
});

export const previewQueryToSummary = (
  preview?: PreviewCmpNewsletterRecipientsQuery["previewCmpNewsletterRecipients"]
): RecipientPreviewSummary | null => {
  if (!preview) return null;

  return {
    totalCandidateCount: preview.totalCandidateCount,
    uniqueValidRecipientCount: preview.uniqueValidRecipientCount,
    invalidRecipientCount: preview.invalidRecipientCount,
    skippedRecipientCount: preview.skippedRecipientCount,
    recipientOverrideUsed: preview.recipientOverrideUsed,
    actualTestRecipientEmail: preview.actualTestRecipientEmail,
    recipientRoutingMode: preview.recipientRoutingMode,
  };
};
